// Icônes référencées par nom (résolues via `lucide-react` au rendu).
import type * as Icons from "lucide-react";

import { APP_ACCESS_ROLES, FLASHPAY_ROLES } from "@/core/auth/roles";
import type { TFunctionType } from "@/core/hooks/use-typed-translation";

export type NavIconName = keyof typeof Icons;

export type NavLeafSubItem = {
  /** Clé i18n (préfixe `nav.`) — jamais de libellé en dur. */
  label: TFunctionType;
  href: string;
  activePath?: string;
  badge?: string;
  badgeClassName?: string;
  target?: "_blank" | "_self" | "_parent" | "_top";
  /**
   * Rôles de realm exigés pour VOIR l'entrée (matrice §6.x du guide) —
   * absent = visible par tout utilisateur authentifié.
   */
  roles?: readonly string[];
};

export type NavGroupSubItem = {
  label: TFunctionType;
  childItems: NavLeafSubItem[];
};

export type NavSubItem = NavLeafSubItem | NavGroupSubItem;

export type NavMenuItem = {
  icon: NavIconName;
  label: TFunctionType;
} & (
  | {
      href: string;
      badge?: string;
      badgeClassName?: string;
      childItems?: never;
      target?: "_blank" | "_self" | "_parent" | "_top";
      roles?: readonly string[];
    }
  | { href?: never; badge?: never; childItems: NavSubItem[] }
);

export type NavGroup = {
  groupLabel?: TFunctionType;
  items: NavMenuItem[];
};

/** Rôles back-office KYC : consultation des identités et dossiers (§6.2). */
const KYC_READ_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.AGENTS,
] as const;

/** Rôles d'administration des agents (§6.2). */
const KYC_ADMIN_ROLES = [FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN] as const;

/** Rôles back-office marchands (§6.6). */
const MERCHANT_ADMIN_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
] as const;

/**
 * Self-service du marchand titulaire. L'agent de vente et le back-office
 * (déjà couvert par « Marchands ») ne voient pas ces feuilles.
 */
const MERCHANT_SELF_SERVICE_ROLES = [FLASHPAY_ROLES.MERCHANT] as const;

/** Rôles back-office tarification (§6.5). */
const FEE_ADMIN_ROLES = [FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN] as const;

/** Rôles gestion des modèles / préférences de notifications (§6.7). */
const NOTIFICATION_ADMIN_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
] as const;

/** Rôles consultation des logs de notifications (§6.7 — inclut le compte technique). */
const NOTIFICATION_LOGS_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

/**
 * Paie de masse et gestion des lots (`/app/payroll`, `/app/payroll/runs`) :
 * marchand titulaire uniquement. Le back-office (superadmin, admin, compte
 * technique) n'y accède pas. Les cagnottes gardent leur propre matrice.
 */
const PAYROLL_BATCH_ROLES = [FLASHPAY_ROLES.MERCHANT] as const;
const PAYROLL_FUNDRAISING_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
] as const;

/** Phase 6 — Rôles consultation du journal comptable (§6.4 — inclut le compte technique). */
const LEDGER_READ_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

/** Phase 6 — Rôles d'administration des référentiels comptables (§6.4). */
const LEDGER_ADMIN_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
] as const;

/** Phase 8 — File d'approbations maker-checker (§6.8 — inclut le compte technique). */
const ADMIN_APPROVALS_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

/** Phase 8 — Journal d'audit et clients API gateway (§6.8 / §6.1). */
const ADMIN_CONSOLE_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
] as const;

/**
 * Configuration système (§6.8) : tout rôle d'accès applicatif sauf le
 * marchand titulaire. Sans cette exclusion, la feuille sans `roles`
 * gardait le menu Administration visible pour `flashpay-merchant`.
 */
const SYSTEM_CONFIGS_ROLES = APP_ACCESS_ROLES.filter(
  (role) => role !== FLASHPAY_ROLES.MERCHANT
);

/** Phase 18 — Coffres d'épargne (savings-service, guide v3.1) : agents de terrain + back-office. */
const SAVINGS_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.AGENTS,
] as const;

/** Phase 18 — Contestations de transfert (file de travail back-office). */
const DISPUTES_ROLES = [FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN] as const;

/** Phase 11 — dashboard live + jauges (guide §7). */
const DASHBOARD_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

/**
 * Portefeuilles et paiement QR : exploitation back-office
 * (même trio que le dashboard). Les coffres, l'e-money et les contestations
 * gardent leur propre matrice.
 */
const WALLET_BO_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

/**
 * Historique des transactions : back-office (vue complète) et marchand
 * titulaire (le serveur ne renvoie que ses opérations).
 */
const WALLET_TX_ROLES = [
  ...WALLET_BO_ROLES,
  FLASHPAY_ROLES.MERCHANT,
] as const;

/** Une feuille est visible si elle n'exige aucun rôle ou si l'utilisateur en a un. */
const leafVisible = (
  leaf: { roles?: readonly string[] },
  realmRoles: readonly string[]
) => !leaf.roles || leaf.roles.some((role) => realmRoles.includes(role));

/** Retire les feuilles interdites et les sous-groupes devenus vides. */
const filterSubItems = (
  items: NavSubItem[],
  realmRoles: readonly string[]
): NavSubItem[] => {
  const visible: NavSubItem[] = [];
  for (const item of items) {
    if ("childItems" in item) {
      const childItems = item.childItems.filter((leaf) =>
        leafVisible(leaf, realmRoles)
      );
      if (childItems.length > 0) {
        visible.push({ label: item.label, childItems });
      }
    } else if (leafVisible(item, realmRoles)) {
      visible.push(item);
    }
  }
  return visible;
};

/** Retire les entrées interdites et les menus parents devenus vides. */
const filterMenuItems = (
  items: NavMenuItem[],
  realmRoles: readonly string[]
): NavMenuItem[] => {
  const visible: NavMenuItem[] = [];
  for (const item of items) {
    if (item.childItems) {
      const childItems = filterSubItems(item.childItems, realmRoles);
      if (childItems.length > 0) {
        visible.push({ icon: item.icon, label: item.label, childItems });
      }
    } else if (leafVisible(item, realmRoles)) {
      visible.push(item);
    }
  }
  return visible;
};

/**
 * Filtre la navigation selon les rôles de realm de l'utilisateur :
 * feuilles sans rôle exigé toujours visibles ; chaque lien interdit est
 * retiré ; sous-groupes, menus et groupes de sidebar disparaissent
 * dès qu'ils ne contiennent plus rien de visible.
 */
export const filterNavItemsByRoles = (
  groups: NavGroup[],
  realmRoles: readonly string[]
): NavGroup[] =>
  groups
    .map((group) => ({
      ...group,
      items: filterMenuItems(group.items, realmRoles),
    }))
    .filter((group) => group.items.length > 0);

/**
 * Navigation applicative Flash Pay (routes sous `/app`). Les libellés sont
 * des clés i18n (`nav.*`) résolues au rendu via `useTypedTranslation`.
 */
export const navItems: NavGroup[] = [
  {
    groupLabel: "nav.groups.overview",
    items: [
      {
        icon: "LayoutDashboard",
        label: "nav.dashboard",
        href: "/app",
        roles: DASHBOARD_ROLES,
      },
      {
        icon: "Wallet",
        label: "nav.wallet",
        childItems: [
          // Clés dans features/wallet/i18n/wallets.json en attendant leur
          // fusion dans src/locales par le coordinateur.
          {
            label: "nav.wallet.wallets" as TFunctionType,
            href: "/app/wallet",
            roles: WALLET_BO_ROLES,
          },
          {
            label: "nav.wallet.transactions" as TFunctionType,
            href: "/app/wallet/transactions",
            roles: WALLET_TX_ROLES,
          },
          {
            label: "nav.wallet.emoney" as TFunctionType,
            href: "/app/wallet/emoney",
            roles: FEE_ADMIN_ROLES,
          },
          {
            label: "nav.wallet.scanTransfer" as TFunctionType,
            href: "/app/wallet/scan-transfer",
            roles: WALLET_BO_ROLES,
          },
          {
            // Phase 18 — Coffres d'épargne (savings-service, guide v3.1).
            label: "nav.wallet.savingsVaults" as TFunctionType,
            href: "/app/wallet/savings-vaults",
            roles: SAVINGS_ROLES,
          },
          {
            // Phase 18 — Contestations de transfert (file de travail BO).
            label: "nav.wallet.disputes" as TFunctionType,
            href: "/app/wallet/disputes",
            roles: DISPUTES_ROLES,
          },
        ],
      },
    ],
  },
  {
    groupLabel: "nav.groups.operations",
    items: [
      {
        // Phase 15 — Delta v2.4 : page agent (QR statique).
        icon: "Scan",
        label: "nav.agents.me" as TFunctionType,
        href: "/app/agents/me",
        roles: [FLASHPAY_ROLES.AGENTS],
      },
      {
        icon: "ShieldCheck",
        label: "nav.kyc",
        childItems: [
          { label: "nav.kyc.onboarding", href: "/app/kyc/onboarding", roles: KYC_READ_ROLES },
          {
            label: "nav.kyc.parties",
            href: "/app/kyc/parties",
            roles: KYC_READ_ROLES,
          },
          {
            label: "nav.kyc.cases",
            href: "/app/kyc/cases",
            roles: KYC_READ_ROLES,
          },
          {
            label: "nav.kyc.agents",
            href: "/app/kyc/agents",
            roles: KYC_ADMIN_ROLES,
          },
        ],
      },
      {
        icon: "Users",
        label: "nav.payroll",
        childItems: [
          // Phase 22 — paie de masse (payroll-service v4.1 §6.11).
          {
            label: "nav.payroll.batches",
            href: "/app/payroll",
            roles: PAYROLL_BATCH_ROLES,
          },
          // Phase 9 — lots de paie (type PAYROLL_CREDIT du wallet-service).
          // Même public que la paie de masse : marchand titulaire uniquement.
          {
            label: "nav.payroll.runs",
            href: "/app/payroll/runs",
            roles: PAYROLL_BATCH_ROLES,
          },
        ],
      },
      {
        icon: "Store",
        label: "nav.merchant",
        childItems: [
          {
            label: "nav.merchant.merchants",
            href: "/app/merchants",
            roles: MERCHANT_ADMIN_ROLES,
          },
          {
            label: "nav.merchant.me",
            href: "/app/merchants/me",
            roles: MERCHANT_SELF_SERVICE_ROLES,
          },
          {
            label: "nav.merchant.subMerchants",
            href: "/app/merchants/me?tab=sub-merchants",
            roles: MERCHANT_SELF_SERVICE_ROLES,
          },
          {
            label: "nav.merchant.stores",
            href: "/app/merchants/me?tab=stores",
            roles: MERCHANT_SELF_SERVICE_ROLES,
          },
          {
            label: "nav.merchant.qrCodes",
            href: "/app/merchants/me?tab=qr-codes",
            roles: MERCHANT_SELF_SERVICE_ROLES,
          },
          {
            label: "nav.merchant.salesAgents",
            href: "/app/merchants/me?tab=sales-agents",
            roles: MERCHANT_SELF_SERVICE_ROLES,
          },
          {
            label: "nav.merchant.sales",
            href: "/app/merchants/me?tab=sales",
            roles: MERCHANT_SELF_SERVICE_ROLES,
          },
          {
            label: "nav.merchant.settlement",
            href: "/app/merchants/me?tab=settlement",
            roles: MERCHANT_SELF_SERVICE_ROLES,
          },
          {
            label: "nav.merchant.feeOverrides",
            href: "/app/merchants/me?tab=fee-overrides",
            roles: MERCHANT_SELF_SERVICE_ROLES,
          },
        ],
      },
      {
        icon: "Percent",
        label: "nav.fees",
        childItems: [
          {
            label: "nav.fees.rules",
            href: "/app/fees/rules",
            roles: FEE_ADMIN_ROLES,
          },
          {
            label: "nav.fees.promotions",
            href: "/app/fees/promotions",
            roles: FEE_ADMIN_ROLES,
          },
          {
            label: "nav.fees.plans",
            href: "/app/fees/plans",
            roles: FEE_ADMIN_ROLES,
          },
          {
            label: "nav.fees.subscriptions",
            href: "/app/fees/subscriptions",
            roles: FEE_ADMIN_ROLES,
          },
        ],
      },
      {
        // Phase 6 — Comptabilité (§6.4).
        icon: "BookOpenCheck",
        label: "nav.ledger" as TFunctionType,
        childItems: [
          {
            label: "nav.ledger.journal" as TFunctionType,
            href: "/app/ledger/journal",
            roles: LEDGER_READ_ROLES,
          },
          {
            label: "nav.ledger.accounts" as TFunctionType,
            href: "/app/ledger/chart-of-accounts",
            roles: LEDGER_ADMIN_ROLES,
          },
          {
            label: "nav.ledger.templates" as TFunctionType,
            href: "/app/ledger/posting-templates",
            roles: LEDGER_ADMIN_ROLES,
          },
          {
            label: "nav.ledger.productCodes" as TFunctionType,
            href: "/app/ledger/product-codes",
            roles: LEDGER_ADMIN_ROLES,
          },
          {
            label: "nav.ledger.providers" as TFunctionType,
            href: "/app/ledger/providers",
            roles: LEDGER_ADMIN_ROLES,
          },
          {
            label: "nav.ledger.mappings" as TFunctionType,
            href: "/app/ledger/product-provider-mappings",
            roles: LEDGER_ADMIN_ROLES,
          },
          {
            label: "nav.ledger.fxRates" as TFunctionType,
            href: "/app/ledger/fx-rates",
            roles: LEDGER_ADMIN_ROLES,
          },
        ],
      },
      {
        icon: "Bell",
        label: "nav.notifications",
        childItems: [
          {
            label: "nav.notifications.templates",
            href: "/app/notifications/templates",
            roles: NOTIFICATION_ADMIN_ROLES,
          },
          {
            label: "nav.notifications.logs",
            href: "/app/notifications/logs",
            roles: NOTIFICATION_LOGS_ROLES,
          },
          {
            label: "nav.notifications.preferences",
            href: "/app/notifications/preferences",
            roles: NOTIFICATION_ADMIN_ROLES,
          },
          {
            // Phase 14 — Delta v2.3 : registre de routage des transports.
            label: "nav.notifications.providers" as TFunctionType,
            href: "/app/notifications/providers",
            roles: NOTIFICATION_ADMIN_ROLES,
          },
        ],
      },
      {
        icon: "HandCoins",
        label: "nav.fundraising",
        childItems: [
          // Phase 9 — collectes / cagnottes (types POT_* du wallet-service).
          // L'entrée placeholder « nav.fundraising.collectors » est retirée :
          // pas d'écran collecteurs au périmètre Phase 9.
          {
            label: "nav.fundraising.campaigns",
            href: "/app/fundraising/campaigns",
            roles: PAYROLL_FUNDRAISING_ROLES,
          },
        ],
      },
      {
        // Phase 8 — Administration & maker-checker (§6.8 / §6.1 gateway).
        icon: "ShieldEllipsis",
        label: "nav.admin" as TFunctionType,
        childItems: [
          {
            label: "nav.admin.approvals" as TFunctionType,
            href: "/app/admin/approvals",
            roles: ADMIN_APPROVALS_ROLES,
          },
          {
            label: "nav.admin.auditLogs" as TFunctionType,
            href: "/app/admin/audit-logs",
            roles: ADMIN_CONSOLE_ROLES,
          },
          {
            // Lecture ouverte à tout rôle d'accès applicatif sauf le marchand (§6.8).
            label: "nav.admin.systemConfigs" as TFunctionType,
            href: "/app/admin/system-configs",
            roles: SYSTEM_CONFIGS_ROLES,
          },
          {
            label: "nav.admin.apiClients" as TFunctionType,
            href: "/app/admin/api-clients",
            roles: ADMIN_CONSOLE_ROLES,
          },
          {
            // Phase 16 — Delta v2.5 : boîte d'envoi (6 services).
            label: "nav.admin.outbox" as TFunctionType,
            href: "/app/admin/outbox",
            roles: ADMIN_CONSOLE_ROLES,
          },
        ],
      },
    ],
  },
  {
    groupLabel: "nav.groups.account",
    items: [
      {
        icon: "UserCog",
        label: "nav.settings",
        childItems: [
          { label: "nav.settings.profile", href: "/app/settings/profile" },
          // Phase 22 — Delta v4.1 : « Mon compte / Sécurité » (changement de
          // mot de passe en session). Visible pour tous les rôles authentifiés
          // de l'app — aucune garde `roles` supplémentaire.
          { label: "nav.account.security", href: "/app/account/security" },
        ],
      },
    ],
  },
];
